import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { isMobileOnly, isIOS } from 'react-device-detect';
import { Box, Container, IconButton, Tooltip, Popover } from '@mui/material';
import { FullscreenSharp, FullscreenExitSharp, RemoveCircleOutline, AddCircleOutline, HelpOutline, GridOn, GridOff, CameraAltOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { checkFile, ConditionalWrapper } from "./Utilities";

export default function Controlbar(props) {

    const { t } = useTranslation()
    const { zoom, zoomUtils, fullscreenHandle, disabled, loading, hasOverview, showOverview, handleTakeSnapshot, handleOverviewClick, imageSource, disableOverview } = props

    const [lastModified, setLastModified] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openLastModifiedTooltip, setOpenLastModifiedTooltip] = useState(false);
    
    const refLastModifiedIcon = useRef(null);
    const refLastModifiedPopover = useRef(null);
    const openLastModifiedPopover = Boolean(anchorEl);
    const idLastModifiedPopover = openLastModifiedPopover ? "last-modified-popover" : undefined;
    const lastModifiedFormatted = lastModified ? dayjs(lastModified).format(t("date.format.full_date_time")) : t("msg.no_data")
    const disableZoomOut = (disabled || (zoom === 1 && showOverview)) || (!hasOverview && zoom === 1) || (disableOverview && zoom === 1)

    // if overview grid is visible than toggle (hide) overview grid, else zoom in
    function onClickOnZoomIn() {
        if (showOverview) {
            handleOverviewClick && handleOverviewClick()
        } else {
            zoomUtils.zoomIn()
        }
    }

    // if current zoom is >100% zoom out, else toggle (open) overview grid
    function onClickOnZoomOut() {
        if (zoom > 1) {
            zoomUtils.zoomOut()
        } else {
            handleOverviewClick && handleOverviewClick()
        }
    }

    function onClickLastModifiedButton(event) {
        event.stopPropagation();
        setOpenLastModifiedTooltip(false);
        setAnchorEl(event.currentTarget);
    };
    
    function onCloseLastModifiedPopover() {
        setAnchorEl(null);
    };
    
    useEffect(() => {
        const checkLastModified = async() => {
            const { exists, contentType, lastModified } = await checkFile(imageSource);
            if (exists && contentType === "image" && lastModified) {
                setLastModified(lastModified);
            }
        }
        checkLastModified();
    }, [imageSource]);

    // check mouse move if leave icon close popover
    useEffect(() => {
        const onLeaveLastModified = (event) => {
            if (!refLastModifiedIcon.current ||
                !refLastModifiedPopover.current ||
                refLastModifiedIcon.current.contains(event.target) ||
                refLastModifiedPopover.current.contains(event.target)) {
                return;
            }
            onCloseLastModifiedPopover();
        };
        document.addEventListener("mousemove", onLeaveLastModified);
        return () => document.removeEventListener("mousemove", onLeaveLastModified);
    }, []);

    const CustomTooltip = ({ children, title }) => {
        return (
            <Tooltip 
                title={title} 
                arrow
                componentsProps={{ popper: { disablePortal: true } }}
            >
                {children}
            </Tooltip>
        )
    }

    return (
        <Container className={isMobileOnly || loading ? "Controlbar-mobileFooter" : "Controlbar-footer"}>
            <Box 
                className="Controlbar-wrapper" 
                sx={{ justifyContent: "center" }}
            >
            
               <Box sx={{
                    display: 'flex',
                    width: '100%'
                }}>
                    
                    <Box className='control-item'>{/* just for spacing */}</Box>

                    <Box className='control-item'>
                        
                        <Box>
                            
                            <ConditionalWrapper
                                condition={!disableZoomOut}
                                wrapper={children => <CustomTooltip title={t("actions.zoom_out")}>{children}</CustomTooltip>}
                            >
                                <Box sx={{ display: 'inline-block' }}>
                                    <IconButton 
                                        className="Controlbar-iconbutton" 
                                        aria-label="zoom out" 
                                        onClick={onClickOnZoomOut}
                                        disabled={disableZoomOut}
                                    >
                                        {disableZoomOut
                                        ? <RemoveCircleOutline sx={{ color: "#606060", fontSize: "1.5rem" }} />
                                        : <RemoveCircleOutline sx={{ color: "white", fontSize: "1.5rem" }} />}
                                    </IconButton>
                                </Box>
                            </ConditionalWrapper>
                            
                            <Box 
                                component="span"
                                className="Controlbar-label"
                                sx={{ color: disabled ? "#606060" : "white" }}
                            >
                                {showOverview ? 10 : parseInt(zoom * 100)}%
                            </Box>

                            <ConditionalWrapper
                                condition={!(zoom === 8 || disabled)}
                                wrapper={children => <CustomTooltip title={t("actions.zoom_in")}>{children}</CustomTooltip>}
                            >
                                <Box sx={{ display: 'inline-block' }}>
                                    <IconButton 
                                        className="Controlbar-iconbutton" 
                                        aria-label="zoom in" 
                                        onClick={onClickOnZoomIn}
                                        disabled={zoom === 8 || disabled}
                                    >
                                        {zoom === 8 || disabled
                                        ? <AddCircleOutline sx={{ color: '#606060', fontSize: "1.5rem" }} />
                                        : <AddCircleOutline sx={{ color: 'white', fontSize: "1.5rem" }} />}
                                    </IconButton>
                                </Box>
                            </ConditionalWrapper>
                        </Box>

                    </Box>

                    <Box className='control-item'>
                        
                        {hasOverview && (
                            <ConditionalWrapper
                                condition={!(disabled || disableOverview)}
                                wrapper={children => <CustomTooltip title={t("actions.overview")}>{children}</CustomTooltip>}
                            >
                                <Box component="span">
                                    <IconButton 
                                        className="Controlbar-iconbutton" 
                                        aria-label="overview" 
                                        onClick={handleOverviewClick}
                                        disabled={disabled || disableOverview}
                                    >   
                                        {disabled || disableOverview 
                                        ? showOverview
                                            ? <GridOff sx={{ color: '#606060', fontSize: "1rem" }} />
                                            : <GridOn sx={{ color: '#606060', fontSize: "1rem" }} />
                                        : showOverview
                                            ? <GridOff sx={{ color: 'white', fontSize: "1rem" }} />
                                            : <GridOn sx={{ color: 'white', fontSize: "1rem" }} />}
                                    </IconButton>
                                </Box>
                            </ConditionalWrapper>
                        )}

                        <ConditionalWrapper
                            condition={!(disabled || showOverview)}
                            wrapper={children => 
                                <Tooltip
                                    open={openLastModifiedTooltip}
                                    onClose={() => setOpenLastModifiedTooltip(false)}
                                    onOpen={() => !openLastModifiedPopover && setOpenLastModifiedTooltip(true)}
                                    title={t("msg.last_update")}
                                    arrow
                                    componentsProps={{ popper: { disablePortal: true } }}
                                >
                                    {children}
                                </Tooltip>
                            }
                        >
                            <Box component="span">
                                <IconButton
                                    ref={refLastModifiedIcon}
                                    className="Controlbar-iconbutton"
                                    aria-label="help-outline"
                                    disabled={disabled || showOverview}
                                    onClick={onClickLastModifiedButton}
                                    sx={{ ...(openLastModifiedPopover && { zIndex: 99999 }) }}
                                >

                                    {disabled || showOverview 
                                    ? <HelpOutline sx={{ color: "#606060", fontSize: "1.1rem" }} />
                                    : <HelpOutline sx={{ color: "white", fontSize: "1.1rem" }} />}

                                </IconButton>

                                <Popover
                                    id={idLastModifiedPopover}
                                    open={openLastModifiedPopover}
                                    disablePortal
                                    anchorEl={anchorEl}
                                    onClose={onCloseLastModifiedPopover}
                                    onMouseLeave={onCloseLastModifiedPopover}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'center'
                                    }}
                                >
                                    <Box
                                        ref={refLastModifiedPopover}
                                        sx={{
                                            p: 1,
                                            maxWidth: "300px",
                                            overflowWrap: "break-word",
                                        }}
                                    >
                                        {`${t("msg.last_update")}: ${lastModifiedFormatted}`}
                                    </Box>
                                </Popover>
                            </Box>
                        </ConditionalWrapper>

                        <ConditionalWrapper
                            condition={!(disabled || showOverview)}
                            wrapper={children => <CustomTooltip title={t("actions.screenshot")}>{children}</CustomTooltip>}
                        >
                            <Box component="span">
                                <IconButton 
                                    className="Controlbar-iconbutton" 
                                    aria-label="snapshot" 
                                    onClick={handleTakeSnapshot}
                                    disabled={disabled || showOverview}
                                >   
                                    {disabled || showOverview
                                    ? <CameraAltOutlined sx={{ color: '#606060', fontSize: "1.2rem", mt: "1px" }} />
                                    : <CameraAltOutlined sx={{ color: 'white', fontSize: "1.2rem", mt: "1px" }} />}
                                </IconButton>
                            </Box>
                        </ConditionalWrapper>

                        {!isIOS && (
                            <ConditionalWrapper
                                condition={!(disabled || showOverview)}
                                wrapper={children => <CustomTooltip title={t("actions.fullscreen")}>{children}</CustomTooltip>}
                            >
                                <Box component="span">
                                    <IconButton 
                                        className="Controlbar-iconbutton" 
                                        aria-label="fullscreen" 
                                        onClick={fullscreenHandle.active ? fullscreenHandle.exit : fullscreenHandle.enter}
                                        disabled={disabled || showOverview}
                                    >
                                        {disabled || showOverview
                                        ? fullscreenHandle.active
                                            ? <FullscreenExitSharp sx={{ color: '#606060', fontSize: "1.5rem", mr: "5px" }} />
                                            : <FullscreenSharp sx={{ color: '#606060', fontSize: "1.5rem", mr: "5px" }} />
                                        : fullscreenHandle.active
                                            ? <FullscreenExitSharp sx={{ color: 'white', fontSize: "1.5rem", mr: "5px" }} />
                                            : <FullscreenSharp sx={{ color: 'white', fontSize: "1.5rem", mr: "5px" }} />}
                                    </IconButton>
                                </Box>
                            </ConditionalWrapper>
                        )}
                
                    </Box>
                </Box>
            </Box>
        </Container>
    )
}